import {UIGameManager} from '../UIGameManager'
import {PIXI} from '@colonist/pixi'
import {UIGamePlayerAvatarView, UIGamePlayerAvatarStatus} from '../ui_game_players/UIGamePlayerAvatarView'
import {ACTIVE_OPACITY, INACTIVE_OPACITY} from '../../ui_utils/Utils'
import {PlayerValidator} from '@colonist/game-mechanic-validators'
import {SectionBgView} from '../../ui_canvas_engine/SectionBgView'
import {TextView} from '../../ui_canvas_engine/TextView'
import {TranslationObject} from '@colonist/i18n'
import {GameUIToolTips} from '../../GameUIToolTips'
import {PlayerColor} from '@colonist/game-states'

export interface PlayerNameTextView {
    playerColor: PlayerColor
    textView: TextView
}

export interface PlayerAvatarContainerViewOptions {
    playerAvatarClickAction?: (playerColor: PlayerColor) => void
    showPlayerNames?: boolean
    removeBg?: boolean
    topPadding?: number
    removePadding?: boolean
    shouldSpaceEvenly?: boolean
}

export class UIGamePlayerAvatarContainerView extends SectionBgView {
    private readonly uiGameManager: UIGameManager
    private readonly topPadding: number
    readonly playerAvatars: UIGamePlayerAvatarView[]

    constructor(uiGameManager: UIGameManager, x: number, y: number, w: number, h: number, players: PlayerColor[], container: PIXI.Container, options?: PlayerAvatarContainerViewOptions) {
        super(x, y, w, h, container, options?.removePadding)
        this.uiGameManager = uiGameManager
        this.playerAvatars = []
        this.topPadding = options?.topPadding ?? 0

        const totalNumberOfPlayers = players.length
        const playerStates: PlayerValidator[] = []
        for(const playerColor of players) {
            const playerState = this.uiGameManager.gameController.getPlayerState(playerColor)
            playerStates.push(playerState)
        }

        const singleLinePlayerAvatarSize = this.height
        const willAllPlayerAvatarsFitInTheContainer = totalNumberOfPlayers * singleLinePlayerAvatarSize <= this.width

        willAllPlayerAvatarsFitInTheContainer ? this.createSingleHorizontalLineOfPlayerAvatars(playerStates, options?.playerAvatarClickAction, options?.showPlayerNames, options?.shouldSpaceEvenly) : this.createStackedLinesOfPlayerAvatars(playerStates, options?.playerAvatarClickAction)

        const drawInitialContainerSize = false
        if(!options?.removeBg) this.drawRoundedBg(undefined, drawInitialContainerSize)
        this.fadeAllPlayers()
    }

    private createSingleHorizontalLineOfPlayerAvatars(playerStates: PlayerValidator[], playerAvatarClickAction?: (playerColor: PlayerColor) => void, showPlayerNames: boolean = false, shouldSpaceEvenly: boolean = true) {
        const avatarSize = this.height - this.topPadding * 2
        const spacingBetweenAvatars = shouldSpaceEvenly ? UIGamePlayerAvatarContainerView.calculateSpacingBetweenAvatars(this.width, avatarSize, playerStates.length) : avatarSize
        let playerAvatarX = shouldSpaceEvenly ? spacingBetweenAvatars * 2 : this.topPadding * 2
        for(const player of playerStates) {
            const playerAvatar = new UIGamePlayerAvatarView(playerAvatarX, this.topPadding, avatarSize, this, player, showPlayerNames)
            if(playerAvatarClickAction && !this.uiGameManager.gameController.isPlayerSpectator) playerAvatar.setClickAction(playerAvatarClickAction)
            this.playerAvatars.push(playerAvatar)
            playerAvatarX += shouldSpaceEvenly ? (spacingBetweenAvatars + avatarSize) : avatarSize
        }
    }

    private createStackedLinesOfPlayerAvatars(playerStates: PlayerValidator[], playerAvatarClickAction?: (playerColor: PlayerColor) => void) {
        const size = this.height - this.topPadding
        for(const [index, playerState] of playerStates.entries()) {
            const playerAvatarX = index == 0 || index == 2 ? 0 : size / 2
            const playerAvatarY = index == 0 || index == 1 ? 0 : size / 2
            const playerAvatar = new UIGamePlayerAvatarView(playerAvatarX, playerAvatarY + this.topPadding, size / 2, this, playerState)
            if(playerAvatarClickAction) playerAvatar.setClickAction(playerAvatarClickAction)
            this.playerAvatars.push(playerAvatar)
        }
    }

    updatePlayerAlpha(player: PlayerColor, opacity: number) {
        this.fadeAllPlayers()
        for(const playerAvatar of this.playerAvatars) {
            if(playerAvatar.playerState.color != player) continue
            playerAvatar.alpha = opacity
        }
    }

    resetAllPlayerAlpha() {
        for(const playerAvatar of this.playerAvatars) {
            playerAvatar.alpha = ACTIVE_OPACITY
        }
    }

    async refreshPlayerIcons() {
        for(const playerAvatar of this.playerAvatars) {
            void playerAvatar.refreshPlayerIcon()
        }
    }

    fadeAllPlayers() {
        for(const playerAvatar of this.playerAvatars) {
            playerAvatar.alpha = INACTIVE_OPACITY
        }
    }

    setPlayerStatus(playerColor: PlayerColor, status: UIGamePlayerAvatarStatus) {
        for(const playerAvatar of this.playerAvatars) {
            if(playerAvatar.destroyed) continue
            if(playerAvatar.playerState.color != playerColor) continue

            playerAvatar.status = status
            return
        }
    }

    setPlayerCreatingCounterOffer(playerColor: PlayerColor) {
        for(const playerAvatar of this.playerAvatars) {
            if(playerAvatar.playerState.color != playerColor) continue
            playerAvatar.showCreatingCounterOfferWaitIcon()
        }
        this.restartAllCreatingCounterOfferIcons()
    }

    removePlayerCreatingCounterOffer(playerColor: PlayerColor) {
        for(const playerAvatar of this.playerAvatars) {
            if(playerAvatar.playerState.color != playerColor) continue

            playerAvatar.hideCreatingCounterOfferWaitIcon()
            return
        }
    }

    private restartAllCreatingCounterOfferIcons() {
        for(const playerAvatar of this.playerAvatars) {
            playerAvatar.restartCreatingCounterOfferWaitIcon()
        }
    }

    setPlayerTooltip(playerColor: PlayerColor, tooltip: TranslationObject[]) {
        const playerAvatar = this.playerAvatars.find((avatarView) => avatarView.playerState.color == playerColor)
        if(playerAvatar === undefined) return

        playerAvatar.setToolTip(tooltip)
    }

    removeAllTooltips() {
        for(const playerAvatar of this.playerAvatars) {
            GameUIToolTips.remove(playerAvatar)
        }
    }

    applySpacingBetweenPlayerAvatarsAndCenterInContainer(spacing: number = 0) {
        const avatarSize = this.playerAvatars[0].width

        const totalAvatarWidth = this.playerAvatars.length * avatarSize
        const totalSpacing = spacing * (this.playerAvatars.length - 1)
        let playerX = (this.width - totalAvatarWidth - totalSpacing) / 2
        for(const playerAvatar of this.playerAvatars) {
            playerAvatar.x = playerX
            playerX += playerAvatar.width + spacing
        }
    }

    removeRoundedBackground() {
        this.removeRoundedBg()
    }

    static calculateSpacingBetweenAvatars(containerWidth: number, playerAvatarWidth: number, numberOfPlayers: number) {
        const numerator = containerWidth - playerAvatarWidth * numberOfPlayers
        const denominator = 3 + numberOfPlayers
        return numerator / denominator
    }

    destroy() {
        super.destroy()
        this.removeAllTooltips()
    }
}
